import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { LuLink, LuCopy, LuExternalLink, LuMessagesSquare, LuMessageCircle, LuUser, LuUnplug } from "react-icons/lu";
import { getChatLink } from "../../Redux/ActionCreators/jobsAction";

// Create-job flow only: the job's shareable links (same sources as the job
// details page — apply_url, chat_url and the on-demand Messenger chat link)
// plus the entry point to try the candidate chat inline.
const LinkCard = ({ icon, title, url, emptyText, connected, onCopy, action }) => (
    <div className={`icv2-link-card${connected ? "" : " icv2-link-card--muted"}`}>
        <span className="icv2-link-card-icon">{icon}</span>
        <div className="icv2-link-card-body">
            <strong>{title}</strong>
            {connected && url ? <code title={url}>{url}</code> : <span>{emptyText}</span>}
        </div>
        <div className="icv2-link-card-actions">
            {connected && url ? (
                <>
                    <button className="icv2-link-btn" onClick={() => onCopy(url, title)}>
                        <LuCopy size={15} /> Copy
                    </button>
                    <a className="icv2-link-btn" href={url} target="_blank" rel="noopener noreferrer">
                        <LuExternalLink size={15} /> Open
                    </a>
                </>
            ) : connected ? (
                action
            ) : (
                <span className="icv2-link-status">
                    <LuUnplug size={13} /> Not connected
                </span>
            )}
        </div>
    </div>
);

const InviteLinksTab = ({ job, onTryIt, onNotify }) => {
    const dispatch = useDispatch();
    const [messengerUrl, setMessengerUrl] = useState("");
    const [messengerLoading, setMessengerLoading] = useState(false);

    const handleCopy = (url, label) => {
        navigator.clipboard.writeText(url);
        onNotify?.(`${label} copied to clipboard!`, "success");
    };

    const handleGetMessengerLink = async () => {
        if (!job?.id || !job?.title) return;
        setMessengerLoading(true);
        const result = await dispatch(getChatLink(job.id, job.title));
        setMessengerLoading(false);
        if (result?.success && result?.chat_url) {
            setMessengerUrl(
                result.chat_url.replace("https://m.me/", `${window.location.origin}/messenger/`) + `/${job.id}`
            );
        } else {
            onNotify?.("Failed to get messenger chat link", "error");
        }
    };

    // Fetch the Messenger link up front so the card shows Copy / Open straight away.
    useEffect(() => {
        if (job?.enable_messenger) handleGetMessengerLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.id, job?.title, job?.enable_messenger]);

    return (
        <div className="icv2-links">
            <LinkCard
                icon={<LuLink size={18} />}
                title="Apply link"
                url={job?.apply_url}
                connected={Boolean(job?.apply_url)}
                emptyText="The apply link isn't available for this job yet."
                onCopy={handleCopy}
            />
            <LinkCard
                icon={<LuMessagesSquare size={18} />}
                title="Chatbot link"
                url={job?.chat_url}
                connected={Boolean(job?.chat_url)}
                emptyText="No Chatbot integration is configured for this workspace, so there is no link to share yet."
                onCopy={handleCopy}
            />
            <LinkCard
                icon={<LuMessageCircle size={18} />}
                title="Facebook Messenger link"
                url={messengerUrl}
                connected={Boolean(job?.enable_messenger)}
                emptyText={
                    !job?.enable_messenger
                        ? "No Facebook Messenger integration is configured for this workspace, so there is no link to share yet."
                        : messengerLoading
                            ? "Getting the Messenger link..."
                            : "Couldn't load the Messenger link."
                }
                onCopy={handleCopy}
                action={
                    messengerLoading ? (
                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    ) : (
                        <button className="icv2-link-btn" onClick={handleGetMessengerLink}>
                            <LuLink size={15} /> Retry
                        </button>
                    )
                }
            />

            <div className="icv2-try-card">
                <span className="icv2-try-card-icon"><LuUser size={18} /></span>
                <div className="icv2-try-card-body">
                    <strong>Experience as candidate</strong>
                    <span>Walk through the application exactly as a candidate would see it.</span>
                </div>
                <button className="icv2-try-btn" onClick={onTryIt}>
                    Try it <LuExternalLink size={15} />
                </button>
            </div>
        </div>
    );
};

export default InviteLinksTab;
